import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SidebarItem from './SidebarItem';
import SidebarDropdown from './SidebarDropdown';
import LanguageSwitcher from '@layouts/langSwitch/componenets/LanguageSwitcher';
import AuthContext from '@providers/context/componenets/AuthContext';
import { useSideBar } from '../hooks/useSideBar';

export default function Sidebar({ isExpanded, onToggle }) {
  const { isAuthenticated, specialRights, userFullName } = useContext(AuthContext);
  const editReportTemplates = useSelector((state) => state.auth.editReportTemplates);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { t } = useTranslation();

  const {
    isMobile,
    handleItemClick,
    toggleSidebar,
    authorizedItems,
    sidebarRef
  } = useSideBar({
    isAuthenticated,
    specialRights,
    editReportTemplates,
    onToggle,
    isExpanded,
    setActiveDropdown,
    location
  });

  if (!isAuthenticated) return null;

  return (
    <>
      {isMobile && !isExpanded && (
        <button
          className="sidebar-toggle-mobile btn btn-light btn-sm"
          onClick={toggleSidebar}
        >
          <i className="bi bi-list fs-5"></i>
        </button>
      )}

      <div ref={sidebarRef} className={`sidebar bg-light border-end ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-toggle-desktop d-flex justify-content-between align-items-center border-bottom p-2">
          <div className="d-flex align-items-center gap-2">
            <div className="user-name text-truncate">
              {isExpanded && <span>{userFullName || t('common.guest')}</span>}
            </div>
            {!isExpanded ? null : <LanguageSwitcher />}
          </div>
          <button
            className="btn btn-sm btn-link"
            onClick={toggleSidebar}
          >
            <i className={`bi bi-chevron-${isExpanded ? 'left' : 'right'}`}></i>
          </button>
        </div>

        <div className="sidebar-content">
          <ul className="nav flex-column">
            {authorizedItems.map((item) => (
              <li key={item.id} className="nav-item">
                {item.type === 'item' && (
                  <SidebarItem
                    item={item}
                    isExpanded={isExpanded}
                    onItemClick={handleItemClick}
                  />
                )}
                {item.type === 'dropdown' && (
                  <SidebarDropdown
                    item={item}
                    isExpanded={isExpanded}
                    isActive={activeDropdown === item.id}
                    onDropdownClick={() => {
                      if (isMobile) {
                        setActiveDropdown(activeDropdown === item.id ? null : item.id);
                      }
                    }}
                    onItemClick={handleItemClick}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="sidebar-footer border-top">
          {isExpanded ? (
            <div className="p-3">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <img
                  src="/images/logo/logo.png"
                  alt="Company Logo"
                  className="sidebar-logo"
                  height="40"
                />
              </div>
              <div className="d-flex justify-content-center gap-2 small">
                <Link to="/about" className="text-muted">{t('common.footer.about')}</Link>
                <span className="text-muted">•</span>
                <Link to="/privacy" className="text-muted">{t('common.footer.privacy')}</Link>
                <span className="text-muted">•</span>
                <Link to="/terms" className="text-muted">{t('common.footer.terms')}</Link>
              </div>
              <div className="text-center text-muted small mt-2">
                {t('common.footer.copyright')}
              </div>
              <div className="text-center text-muted small mt-2">
                SOFIS - HR Version: 2.2.77
              </div>
            </div>
          ) : (
            <div className="p-2 text-center">
              <img
                src="/images/logo/small.logo.jpg"
                alt="Logo"
                className="sidebar-logo-small"
                height="30"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
} 